import { CSSObject } from "@emotion/react";
import { constants, typographyStyles } from "@newsela/angelou";

const colors = constants.colors;
const coreBlue = colors.ui.core[500];
const neutral400 = colors.ui.neutral[400];
const white = colors.ui.white[500];

export const CELL_SIZE_MAX = 56;
export const CELL_SIZE_MIN = 28;
export const GRID_MAX_WIDTH = 660;

const CELL_MARGIN = 2;

export const containerStyles: CSSObject = {
  display: "flex",
  flexDirection: "column",
  padding: "16px",
};

export const cluesSection: CSSObject = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "32px",
  margin: "24px 0",
};

export const clueColumn: CSSObject = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

export const clueColumnTitle: CSSObject = {
  ...typographyStyles.heading6Obj,
  margin: "0 0 4px 0",
};

export const clueListStyle: CSSObject = {
  margin: 0,
  padding: 0,
  listStyle: "none",
  ...typographyStyles.textSObj,

  "& li": {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginBottom: "2px",
  },

  "& li::before": {
    content: "attr(value) '.'",
    fontWeight: 700,
    flexShrink: 0,
  },
};

export const clueTextarea: CSSObject = {
  border: `1px solid ${neutral400}`,
  borderRadius: "4px",
  padding: "2px 6px",
  resize: "none",
  width: "100%",
  maxWidth: "320px",
  ...typographyStyles.textSObj,
};

export const clueText: CSSObject = {
  margin: 0,
  ...typographyStyles.textSObj,
};

export const gridContainer: CSSObject = {
  display: "flex",
  justifyContent: "center",
  padding: "8px 0",
};

export const crosswordStyles = ({
  gridWidth,
  gridHeight,
}: {
  gridWidth: number;
  gridHeight: number;
}) => {
  const maxDimension = Math.max(gridWidth, gridHeight);
  const fittedSize = Math.floor(GRID_MAX_WIDTH / maxDimension) - CELL_MARGIN;
  const cellSize = Math.max(CELL_SIZE_MIN, Math.min(CELL_SIZE_MAX, fittedSize));
  const totalCellSize = cellSize + CELL_MARGIN;

  const crosswordGrid: CSSObject = {
    display: "inline-grid",
    gap: 0,
    gridTemplateColumns: `repeat(${gridWidth}, ${totalCellSize}px)`,
    gridTemplateRows: `repeat(${gridHeight}, ${totalCellSize}px)`,
  };

  const emptyCellBase = {
    margin: "1px",
    width: `${cellSize}px`,
    height: `${cellSize}px`,
  };

  const gridCellBase: CSSObject = {
    border: `2px solid ${coreBlue}`,
    borderRadius: "8px",
    position: "relative",
    backgroundColor: white,
    margin: "1px",
    width: `${cellSize}px`,
    height: `${cellSize}px`,
  };

  const getCellNumber: CSSObject = {
    position: "absolute",
    top: "2px",
    left: cellSize >= 40 ? "4px" : "2px",
    fontSize: cellSize >= 40 ? "11px" : "8px",
    fontWeight: 700,
    color: coreBlue,
    lineHeight: 1,
  };

  const getCellInput: CSSObject = {
    width: "100%",
    height: "100%",
    border: "none",
    background: "transparent",
    textAlign: "center",
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: cellSize >= 40 ? "20px" : "14px",
    color: coreBlue,
    padding: 0,
    outline: "none",
    caretColor: coreBlue,
  };

  const getCellHint: CSSObject = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: cellSize >= 40 ? "20px" : "14px",
    color: coreBlue,
  };

  return {
    crosswordGrid,
    emptyCellBase,
    gridCellBase,
    getCellNumber,
    getCellInput,
    getCellHint,
  };
};
